import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import {
  Plus,
  Trash2,
  Save,
  Package,
  Building2,
  Wallet,
  Receipt,
  Calculator,
  AlertCircle,
  Info,
  CreditCard,
} from "lucide-react";
import { fetchCompanyBankAccounts } from "./Trade/bankAccountsFetch";
import "../Product/PurchasesQuickEntry.css";
import "../Product/customers.css";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function round2(n) {
  return Number(Number(n || 0).toFixed(2));
}

export default function PurchasesQuickEntry() {
  const { companyId } = useCompany();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [isPaid, setIsPaid] = useState(false);

  const [purchaseDate, setPurchaseDate] = useState(todayISO());
  const [warehouseId, setWarehouseId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [bankAccountId, setBankAccountId] = useState("");
  const [notes, setNotes] = useState("");

  const [vatExempt, setVatExempt] = useState(true);
  const [whtExempt, setWhtExempt] = useState(true);

  const [vatRate, setVatRate] = useState(7.5);
  const [whtRate, setWhtRate] = useState(0);

  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);

  const [lines, setLines] = useState([
    {
      item_id: "",
      quantity: "1",
      unit_cost: "0",
      description: "",
      expense_account_id: "",
    },
  ]);

  useEffect(() => {
    (async () => {
      if (!companyId) return;

      const { data: wh } = await supabase
        .from("warehouses")
        .select("id,name,is_default")
        .eq("company_id", companyId)
        .order("name", { ascending: true });

      setWarehouses(wh || []);
      const def = (wh || []).find((w) => w.is_default);
      if (def?.id) setWarehouseId(def.id);

      const { data: v } = await supabase
        .from("vendors")
        .select("id,name,company_name")
        .eq("company_id", companyId)
        .order("name", { ascending: true });

      setVendors(v || []);

      const { data: it } = await supabase
        .from("inventory_items")
        .select("id,sku,name,unit,item_type")
        .eq("company_id", companyId)
        .eq("is_active", true)
        .order("name", { ascending: true });

      setItems(it || []);

      const banks = await fetchCompanyBankAccounts(companyId);
      setBankAccounts(banks || []);

      const { data: exp, error: expErr } = await supabase
        .from("chart_of_accounts")
        .select("id, account_number, name, type")
        .eq("company_id", companyId)
        .eq("type", "Expense")
        .order("name", { ascending: true });

      if (expErr) console.error("Expense accounts fetch error:", expErr);
      setExpenseAccounts(exp || []);
    })();
  }, [companyId]);

  function setLine(i, patch) {
    setLines((p) => p.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setLines((p) => [
      ...p,
      {
        item_id: "",
        quantity: "1",
        unit_cost: "0",
        description: "",
        expense_account_id: "",
      },
    ]);
  }

  function removeLine(i) {
    setLines((p) => p.filter((_, idx) => idx !== i));
  }

  const subtotal = useMemo(() => {
    return round2(
      lines.reduce((s, l) => {
        const q = Number(l.quantity || 0);
        const c = Number(l.unit_cost || 0);
        return s + (q > 0 ? q * c : 0);
      }, 0)
    );
  }, [lines]);

  const taxSummary = useMemo(() => {
    const activeVatRate = vatExempt ? 0 : Number(vatRate || 0);
    const activeWhtRate = whtExempt ? 0 : Number(whtRate || 0);

    const vat = round2(subtotal * (activeVatRate / 100));
    const gross = round2(subtotal + vat);
    const wht = round2(subtotal * (activeWhtRate / 100));
    const net = round2(gross - wht);

    return {
      subtotal,
      vatRate: activeVatRate,
      vatAmount: vat,
      grossTotal: gross,
      whtRate: activeWhtRate,
      whtAmount: wht,
      netAmount: net,
    };
  }, [subtotal, vatRate, whtRate, vatExempt, whtExempt]);

  async function postPurchase() {
    setErr("");
    if (!companyId) return setErr("Missing company.");
    if (!warehouseId) return setErr("Select warehouse.");
    if (isPaid && !bankAccountId) {
      return setErr("Select bank account for paid purchases.");
    }

    const cleanLines = lines
      .map((l) => {
        const qty = Number(l.quantity || 0);
        const unitCost = Number(l.unit_cost || 0);
        return {
          item_id: l.item_id || null,
          quantity: qty,
          unit_cost: unitCost,
          description: l.description?.trim() || null,
          expense_account_id: l.expense_account_id || null,
          line_total: qty > 0 ? qty * unitCost : 0,
        };
      })
      .filter((l) => l.quantity > 0 && (l.item_id || l.description));

    if (!cleanLines.length) return setErr("Add at least one line.");

    const missingExpense = cleanLines.find(
      (l) => !l.item_id && l.description && !l.expense_account_id
    );
    if (missingExpense) {
      return setErr(
        "For description-only lines, select an Expense account (to post the journal properly)."
      );
    }

    const invalidCost = cleanLines.find((l) => l.unit_cost < 0);
    if (invalidCost) return setErr("Unit cost cannot be negative.");

    const headerTotal = cleanLines.reduce(
      (s, l) => s + Number(l.line_total || 0),
      0
    );
    if (!(headerTotal > 0)) return setErr("Total must be > 0.");

    setBusy(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/purchase-post`;
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          purchase_date: purchaseDate,
          vendor_id: vendorId || null,
          payment_status: isPaid ? "PAID" : "UNPAID",
          bank_account_id: isPaid ? bankAccountId : null,
          warehouse_id: warehouseId,
          notes: notes?.trim() || null,
          total_amount: headerTotal,

          vat_rate: Number(taxSummary.vatRate || 0) / 100,
          vat_amount: taxSummary.vatAmount,
          wht_rate: Number(taxSummary.whtRate || 0) / 100,
          wht_amount: taxSummary.whtAmount,
          gross_total: taxSummary.grossTotal,
          net_amount: taxSummary.netAmount,
          tax_json: {
            subtotal: taxSummary.subtotal,
            vat_exempt: vatExempt,
            vat_rate: Number(taxSummary.vatRate || 0) / 100,
            vat_amount: taxSummary.vatAmount,
            wht_exempt: whtExempt,
            wht_rate: Number(taxSummary.whtRate || 0) / 100,
            wht_amount: taxSummary.whtAmount,
            gross_total: taxSummary.grossTotal,
            net_amount: taxSummary.netAmount,
          },
          lines: cleanLines,
        }),
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw || "Unknown error" };
      }

      if (!res.ok) throw new Error(json?.error || "Failed to post purchase.");

      alert(
        `Purchase posted. Gross ₦${Number(
          taxSummary.grossTotal || 0
        ).toLocaleString()}`
      );

      setNotes("");
      setVendorId("");
      setBankAccountId("");
      setIsPaid(false);
      setVatRate(7.5);
      setWhtRate(0);
      setVatExempt(true);
      setWhtExempt(true);
      setLines([
        {
          item_id: "",
          quantity: "1",
          unit_cost: "0",
          description: "",
          expense_account_id: "",
        },
      ]);
    } catch (e) {
      setErr(e.message || "Error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pqe-page modern">
      <div className="pqe-hero">
        <div>
          <p className="pqe-eyebrow">Purchases</p>

          <h1>Quick Purchase Entry</h1>

          <p>
            Record supplier purchases, inventory costs, operating expenses,
            VAT/WHT and payment information in one simple workflow.
          </p>
        </div>

        <div className="pqe-hero-total">
          <span>Gross Purchase Total</span>
          <strong>
            ₦{Number(taxSummary.grossTotal || 0).toLocaleString()}
          </strong>
        </div>
      </div>

      {err && (
        <div className="pqe-banner error">
          <AlertCircle size={16} />
          {err}
        </div>
      )}

      <div className="pqe-summary-grid">
        <div className="pqe-summary-card">
          <div className="icon-wrap">
            <Receipt size={18} />
          </div>

          <div>
            <span>Subtotal</span>
            <strong>
              ₦{Number(taxSummary.subtotal || 0).toLocaleString()}
            </strong>
          </div>
        </div>

        <div className="pqe-summary-card">
          <div className="icon-wrap">
            <Calculator size={18} />
          </div>

          <div>
            <span>VAT</span>
            <strong>
              ₦{Number(taxSummary.vatAmount || 0).toLocaleString()}
            </strong>
          </div>
        </div>

        <div className="pqe-summary-card">
          <div className="icon-wrap">
            <Wallet size={18} />
          </div>

          <div>
            <span>Net Payable</span>
            <strong>
              ₦{Number(taxSummary.netAmount || 0).toLocaleString()}
            </strong>
          </div>
        </div>

        <div className="pqe-summary-card">
          <div className="icon-wrap">
            <CreditCard size={18} />
          </div>

          <div>
            <span>Status</span>
            <strong>{isPaid ? "PAID" : "UNPAID"}</strong>
          </div>
        </div>
      </div>

      <section className="pqe-card modern-card">
        <div className="section-title">
          <Building2 size={18} />
          Purchase Details
        </div>

        <div className="pqe-grid">
          <div className="pqe-field">
            <label>Date</label>

            <input
              className="pqe-input"
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              disabled={busy}
            />

            <small>Date the supplier invoice or purchase occurred.</small>
          </div>

          <div className="pqe-field">
            <label>Warehouse</label>

            <select
              className="pqe-input"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              disabled={busy}
            >
              <option value="">Select warehouse…</option>

              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                  {w.is_default ? " (Default)" : ""}
                </option>
              ))}
            </select>

            <small>
              Inventory purchased will be added into this warehouse/location.
            </small>
          </div>

          <div className="pqe-field pqe-span-2">
            <label>Vendor / Supplier</label>

            <select
              className="pqe-input"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              disabled={busy}
            >
              <option value="">Select vendor…</option>

              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                  {v.company_name ? ` - ${v.company_name}` : ""}
                </option>
              ))}
            </select>

            <small>Supplier this purchase was made from.</small>
          </div>
        </div>

        <div className="pqe-payment-box">
          <label className="pqe-check">
            <input
              type="checkbox"
              checked={isPaid}
              onChange={(e) => setIsPaid(e.target.checked)}
              disabled={busy}
            />
            Mark Purchase as Paid
          </label>

          <p>
            If enabled, KostPadi will immediately reduce your selected bank
            account balance.
          </p>

          {isPaid && (
            <select
              className="pqe-input pqe-bank"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              disabled={busy}
            >
              <option value="">Select bank account…</option>

              {bankAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.account_number ? `${a.account_number} - ` : ""}
                  {a.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </section>

      <section className="pqe-card modern-card">
        <div className="section-title">
          <Calculator size={18} />
          Tax Configuration
        </div>

        <div className="pqe-info">
          <Info size={15} />
          VAT increases the gross amount payable while WHT reduces the amount
          payable to the supplier.
        </div>

        <div className="pqe-grid">
          <div className="pqe-field">
            <label>VAT Rate (%)</label>

            <input
              className="pqe-input"
              type="number"
              min="0"
              step="0.01"
              value={vatRate}
              onChange={(e) => setVatRate(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="pqe-field">
            <label>WHT Rate (%)</label>

            <input
              className="pqe-input"
              type="number"
              min="0"
              step="0.01"
              value={whtRate}
              onChange={(e) => setWhtRate(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="pqe-field">
            <label>VAT Exempt</label>

            <label className="pqe-check">
              <input
                type="checkbox"
                checked={vatExempt}
                onChange={(e) => setVatExempt(e.target.checked)}
                disabled={busy}
              />
              This purchase is VAT exempt
            </label>
          </div>

          <div className="pqe-field">
            <label>WHT Exempt</label>

            <label className="pqe-check">
              <input
                type="checkbox"
                checked={whtExempt}
                onChange={(e) => setWhtExempt(e.target.checked)}
                disabled={busy}
              />
              This purchase is WHT exempt
            </label>
          </div>
        </div>
      </section>

      <section className="pqe-card modern-card">
        <div className="section-title">
          <Package size={18} />
          Purchase Lines
        </div>

        <div className="pqe-info">
          <Info size={15} />
          Use inventory items for stock purchases. Use description-only lines
          for operating expenses or non-inventory purchases.
        </div>

        <div className="pqe-lines modern-lines">
          {lines.map((l, idx) => (
            <div key={idx} className="pqe-line modern-line">
              <div className="line-top">
                <strong>Line {idx + 1}</strong>

                <button
                  className="pqe-icon-btn"
                  onClick={() => removeLine(idx)}
                  disabled={busy || lines.length === 1}
                  title="Remove line"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="pqe-line-grid">
                <select
                  className="pqe-input"
                  value={l.item_id}
                  onChange={(e) =>
                    setLine(idx, {
                      item_id: e.target.value,
                      description: "",
                      expense_account_id: "",
                    })
                  }
                  disabled={busy}
                >
                  <option value="">Select inventory item… (optional)</option>

                  {items.map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.sku} - {it.name} ({it.unit}) [{it.item_type}]
                    </option>
                  ))}
                </select>

                {!l.item_id && (
                  <>
                    <input
                      className="pqe-input"
                      placeholder="Expense description"
                      value={l.description}
                      onChange={(e) =>
                        setLine(idx, {
                          description: e.target.value,
                        })
                      }
                      disabled={busy}
                    />

                    <select
                      className="pqe-input"
                      value={l.expense_account_id}
                      onChange={(e) =>
                        setLine(idx, {
                          expense_account_id: e.target.value,
                        })
                      }
                      disabled={busy}
                    >
                      <option value="">Select expense account…</option>

                      {expenseAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.account_number ? `${a.account_number} - ` : ""}
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                <input
                  className="pqe-input"
                  type="number"
                  placeholder="Quantity"
                  value={l.quantity}
                  onChange={(e) => setLine(idx, { quantity: e.target.value })}
                  disabled={busy}
                />

                <input
                  className="pqe-input"
                  type="number"
                  placeholder="Unit cost"
                  value={l.unit_cost}
                  onChange={(e) => setLine(idx, { unit_cost: e.target.value })}
                  disabled={busy}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="pqe-actions">
          <button className="pqe-btn" onClick={addLine} disabled={busy}>
            <Plus size={16} />
            Add Another Line
          </button>
        </div>
      </section>

      <section className="pqe-card modern-card">
        <div className="section-title">
          <Receipt size={18} />
          Notes
        </div>

        <textarea
          className="pqe-input pqe-textarea"
          rows={4}
          placeholder="Additional notes about this purchase..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={busy}
        />
      </section>

      <div className="pqe-sticky-footer">
        <div className="sticky-summary">
          <span>Gross Total</span>

          <strong>
            ₦{Number(taxSummary.grossTotal || 0).toLocaleString()}
          </strong>
        </div>

        <button
          className="btn-primary large"
          onClick={postPurchase}
          disabled={busy}
        >
          <Save size={18} />
          {busy ? "Posting Purchase…" : "Post Purchase"}
        </button>
      </div>
    </div>
  );
}
